import React, { useEffect, useState } from 'react';
import {
  Accordion,
  Button,
  Card,
  Container,
  Form,
  Table,
} from 'react-bootstrap';
import { Helmet } from 'react-helmet';
import DatePicker from 'react-datepicker';
import { fi } from 'date-fns/locale';
import * as signalR from '@microsoft/signalr';
import { Icon } from '@lomarengas/design-system';
import { format } from 'date-fns';
import { InputCheck, SectionHeader } from '../../common/components';
import { TagSelector } from '../../common/components/TagSelector';
import { AccommodationSelector } from '../../common/components/AccommodationSelector';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import {
  changePricesForSeasonRates,
  clearSeasonRateBatchUpdateDetails,
  fetchLastTagOperation,
  fetchSeasonRateBatchUpdateDetails,
  type LastTagOperationDto,
} from './rateToolsSlice';
import LastTagOperationInfo from './LastTagOperationInfo';
import signalRPricingService from '../../common/hubs/signalRPricingService';
import { formatDateToFinnish } from '../../common/utils/dateUtils';
import {
  SEASON_RATE_BATCH_OPERATION_TYPE,
  SEASON_RATE_BATCH_OPERATION_TYPE_DESCRIPTIONS,
} from '../../common/constants';

interface CopyMessage {
  accommodationNumber?: string;
  accommodationName?: string;
  seasonRatesChangedCount?: string;
}

const ChangeSeasonRatePrices = () => {
  const dispatch = useAppDispatch();
  const [selectedTagId, setSelectedTagId] = useState<string | null>(null);
  const [lastTagOperation, setLastTagOperation] =
    useState<LastTagOperationDto | null>(null);
  const [selectedAccommodationId, setSelectedAccommodationId] = useState<
    string | null
  >(null);
  const [selectedStartDateForAll, setSelectedStartDateForAll] =
    useState<Date | null>(null);
  const [selectedEndDateForAll, setSelectedEndDateForAll] =
    useState<Date | null>(null);
  const [selectedStartDateForTag, setSelectedStartDateForTag] =
    useState<Date | null>(null);
  const [selectedEndDateForTag, setSelectedEndDateForTag] =
    useState<Date | null>(null);
  const [
    selectedStartDateForAccommodation,
    setSelectedStartDateForAccommodation,
  ] = useState<Date | null>(null);
  const [selectedEndDateForAccommodation, setSelectedEndDateForAccommodation] =
    useState<Date | null>(null);
  const [
    selectedPriceChangePercentForAll,
    setSelectedPriceChangePercentForAll,
  ] = useState<number | null>(null);
  const [
    selectedPriceChangePercentForTag,
    setSelectedPriceChangePercentForTag,
  ] = useState<number | null>(null);
  const [
    selectedPriceChangePercentForAccommodation,
    setSelectedPriceChangePercentForAccommodation,
  ] = useState<number | null>(null);
  const [
    selectedPriceChangeAbsoluteForAll,
    setSelectedPriceChangeAbsoluteForAll,
  ] = useState<number | null>(null);
  const [
    selectedPriceChangeAbsoluteForTag,
    setSelectedPriceChangeAbsoluteForTag,
  ] = useState<number | null>(null);
  const [
    selectedPriceChangeAbsoluteForAccommodation,
    setSelectedPriceChangeAbsoluteForAccommodation,
  ] = useState<number | null>(null);
  const [selectedDoUpdatePricesForAll, setSelectedDoUpdatePricesForAll] =
    useState<boolean | null>(true);
  const [selectedDoUpdatePricesForTag, setSelectedDoUpdatePricesForTag] =
    useState<boolean | null>(true);
  const [
    selectedDoUpdatePricesForAccommodation,
    setSelectedDoUpdatePricesForAccommodation,
  ] = useState<boolean | null>(true);
  const [
    selectedDoUpdateVatPurchasePricesForAll,
    setSelectedDoUpdateVatPurchasePricesForAll,
  ] = useState<boolean | null>(true);
  const [
    selectedDoUpdateVatPurchasePricesForTag,
    setSelectedDoUpdateVatPurchasePricesForTag,
  ] = useState<boolean | null>(true);
  const [
    selectedDoUpdateVatPurchasePricesForAccommodation,
    setSelectedDoUpdateVatPurchasePricesForAccommodation,
  ] = useState<boolean | null>(true);

  const [
    selectedDoUpdateWithoutVatPurchasePricesForAll,
    setSelectedDoUpdateWithoutVatPurchasePricesForAll,
  ] = useState<boolean | null>(true);
  const [
    selectedDoUpdateWithoutVatPurchasePricesForTag,
    setSelectedDoUpdateWithoutVatPurchasePricesForTag,
  ] = useState<boolean | null>(true);
  const [
    selectedDoUpdateWithoutVatPurchasePricesForAccommodation,
    setSelectedDoUpdateWithoutVatPurchasePricesForAccommodation,
  ] = useState<boolean | null>(true);

  const [copyMessages, setCopyMessages] = useState<CopyMessage[]>([]);
  const [isCopying, setIsCopying] = useState<boolean>(false);
  const seasonRateBatchUpdateDetails = useAppSelector(
    state => state.rateTools.seasonRateBatchUpdateDetails,
  );

  useEffect(() => {
    dispatch(clearSeasonRateBatchUpdateDetails());
    dispatch(
      fetchSeasonRateBatchUpdateDetails(
        SEASON_RATE_BATCH_OPERATION_TYPE.PriceChange,
      ),
    );
  }, [dispatch]);

  useEffect(() => {
    if (
      signalRPricingService.connection.state ===
      signalR.HubConnectionState.Disconnected
    ) {
      console.log('start connection');
      signalRPricingService.startConnection();
    }

    signalRPricingService.onReceiveMessage = (
      accommodationNumber: string,
      accommodationName: string,
      seasonRatesChangedCount: string,
    ) => {
      setCopyMessages(prevCopyMessages => [
        ...prevCopyMessages,
        { accommodationNumber, accommodationName, seasonRatesChangedCount },
      ]);
    };
  }, []);

  const handleTagSelected = async (tagId: string) => {
    setSelectedTagId(tagId);

    if (tagId === null) {
      setSelectedStartDateForTag(null);
      setSelectedEndDateForTag(null);
      setLastTagOperation(null);
    } else {
      const result = await dispatch(
        fetchLastTagOperation({
          tagId,
          operationType: SEASON_RATE_BATCH_OPERATION_TYPE.PriceChange,
        }),
      );
      setLastTagOperation(
        (result.payload as LastTagOperationDto | null) ?? null,
      );
    }
  };

  const handleAccommodationSelected = (accommodationId: string) => {
    setSelectedAccommodationId(accommodationId);

    if (accommodationId === null) {
      setSelectedStartDateForAccommodation(null);
      setSelectedEndDateForAccommodation(null);
    }
  };

  const handlePriceChangesForAll = async () => {
    if (
      selectedStartDateForAll &&
      selectedEndDateForAll &&
      (selectedDoUpdatePricesForAll ||
        selectedDoUpdateVatPurchasePricesForAll ||
        selectedDoUpdateWithoutVatPurchasePricesForAll)
    ) {
      setIsCopying(true);
      await dispatch(
        changePricesForSeasonRates({
          changePricesFrom: formatDateToFinnish(selectedStartDateForAll),
          changePricesTo: formatDateToFinnish(selectedEndDateForAll),
          priceChangePercent: selectedPriceChangePercentForAll,
          priceChangeAbsolute: selectedPriceChangeAbsoluteForAll,
          updatePrices: selectedDoUpdatePricesForAll,
          updateVatPurchasePrices: selectedDoUpdateVatPurchasePricesForAll,
          updateWithoutVatPurchasePrices:
            selectedDoUpdateWithoutVatPurchasePricesForAll,
        }),
      );
      setIsCopying(false);
    }
  };

  const handlePriceChangesForTag = async () => {
    if (
      selectedTagId &&
      selectedStartDateForTag &&
      selectedEndDateForTag &&
      (selectedDoUpdatePricesForTag ||
        selectedDoUpdateVatPurchasePricesForTag ||
        selectedDoUpdateWithoutVatPurchasePricesForTag)
    ) {
      setIsCopying(true);
      await dispatch(
        changePricesForSeasonRates({
          tagId: selectedTagId,
          changePricesFrom: formatDateToFinnish(selectedStartDateForTag),
          changePricesTo: formatDateToFinnish(selectedEndDateForTag),
          priceChangePercent: selectedPriceChangePercentForTag,
          priceChangeAbsolute: selectedPriceChangeAbsoluteForTag,
          updatePrices: selectedDoUpdatePricesForTag,
          updateVatPurchasePrices: selectedDoUpdateVatPurchasePricesForTag,
          updateWithoutVatPurchasePrices:
            selectedDoUpdateWithoutVatPurchasePricesForTag,
        }),
      );
      setIsCopying(false);
    }
  };

  const handlePriceChangesForAccommodation = async () => {
    if (
      selectedAccommodationId &&
      selectedStartDateForAccommodation &&
      selectedEndDateForAccommodation &&
      (selectedDoUpdatePricesForAccommodation ||
        selectedDoUpdateVatPurchasePricesForAccommodation ||
        selectedDoUpdateWithoutVatPurchasePricesForAccommodation)
    ) {
      setIsCopying(true);
      await dispatch(
        changePricesForSeasonRates({
          accommodationId: selectedAccommodationId,
          changePricesFrom: formatDateToFinnish(
            selectedStartDateForAccommodation,
          ),
          changePricesTo: formatDateToFinnish(selectedEndDateForAccommodation),
          priceChangePercent: selectedPriceChangePercentForAccommodation,
          priceChangeAbsolute: selectedPriceChangeAbsoluteForAccommodation,
          updatePrices: selectedDoUpdatePricesForAccommodation,
          updateVatPurchasePrices:
            selectedDoUpdateVatPurchasePricesForAccommodation,
          updateWithoutVatPurchasePrices:
            selectedDoUpdateWithoutVatPurchasePricesForAccommodation,
        }),
      );
      setIsCopying(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Hinnoittelu - Hintojen korotus / alennus</title>
      </Helmet>

      <Container className="container-content">
        <SectionHeader
          Breadcrumbs={['Hinnoittelu']}
          Title="Hintojen korotus / alennus"
        />
        <div className="my-48">
          <div
            className="border bg-white rounded shadow-lg mx-auto p-24 p-xl-32"
            style={{ maxWidth: 640 }}
          >
            <div className="d-flex align-items-start align-items-sm-center mb-24 mb-xl-32">
              <div className="d-block bg-primary fs-3 me-24 px-16 py-8 rounded text-center text-white">
                <Icon Name="intersect" />
              </div>
              <div className="flex-grow-1">
                <h2 className="h5 mb-0">Kaikki kohteet</h2>
                <p className="text-gray-600 mb-0">
                  Hintojen muutos kaikille kohteille
                </p>
              </div>
            </div>
            <p className="text-gray-600 mt-16 mb-16">
              Hinnan muutosten alkupäivämäärä
            </p>
            <DatePicker
              locale={fi}
              dateFormat="dd.MM.yyyy"
              onChange={date => setSelectedStartDateForAll(date)}
              className="form-control"
              showWeekNumbers
              showYearDropdown
              scrollableYearDropdown
              selected={selectedStartDateForAll}
            />
            <p className="text-gray-600 mt-16 mb-16">
              Hinnan muutosten päättymispäivämäärä
            </p>
            <DatePicker
              locale={fi}
              dateFormat="dd.MM.yyyy"
              minDate={selectedStartDateForAll ?? null}
              onChange={date => setSelectedEndDateForAll(date)}
              className="form-control"
              showYearDropdown
              showWeekNumbers
              disabled={!selectedStartDateForAll}
              scrollableYearDropdown
              selected={selectedEndDateForAll}
            />
            <p className="text-gray-600 mt-16 mb-16">Hinnan muutosprosentti</p>
            <Form.Control
              type="number"
              step="0.01"
              id="priceChangePercent"
              name="priceChangePercent"
              className="mb-16"
              onChange={e =>
                setSelectedPriceChangePercentForAll(parseFloat(e.target.value))
              }
            />
            <p className="text-gray-600 mt-16 mb-16">Hinnan kiinteä muutos</p>
            <Form.Control
              type="number"
              id="priceChangeAbsolute"
              name="priceChangeAbsolute"
              className="mb-16"
              onChange={e =>
                setSelectedPriceChangeAbsoluteForAll(parseFloat(e.target.value))
              }
            />
            <InputCheck
              Id="doUpdatePrices"
              Name="doUpdatePrices"
              Label="Korota myyntihinnat"
              HandleChange={e =>
                setSelectedDoUpdatePricesForAll(e.target.checked)
              }
              Checked={selectedDoUpdatePricesForAll}
              IsRequired={false}
            />
            <InputCheck
              Id="doUpdateVatPurchasePrices"
              Name="doUpdateVatPurchasePrices"
              Label="Korota alv velvollisten omistajien ostohinnat"
              HandleChange={e =>
                setSelectedDoUpdateVatPurchasePricesForAll(e.target.checked)
              }
              Checked={selectedDoUpdateVatPurchasePricesForAll}
              IsRequired={false}
            />
            <InputCheck
              Id="doUpdateWithoutVatPurchasePrices"
              Name="doUpdateWithoutVatPurchasePrices"
              Label="Korota alvittomien omistajien ostohinnat"
              HandleChange={e =>
                setSelectedDoUpdateWithoutVatPurchasePricesForAll(
                  e.target.checked,
                )
              }
              Checked={selectedDoUpdateWithoutVatPurchasePricesForAll}
              IsRequired={false}
            />
            <Button
              variant="primary"
              className="mt-16 mb-16 w-100"
              onClick={handlePriceChangesForAll}
              disabled={isCopying}
            >
              {isCopying
                ? 'Muutetaan hintoja...'
                : 'Muuta hinnat kaikille kohteille'}
            </Button>
          </div>
        </div>
        <div className="my-48">
          <div
            className="border bg-white rounded shadow-lg mx-auto p-24 p-xl-32"
            style={{ maxWidth: 640 }}
          >
            <TagSelector
              filterActiveAccommodations
              Subtitle="Hintojen muutos tagin kohteille"
              Title="Valitse tagi"
              onTagSelected={handleTagSelected}
            />
            <LastTagOperationInfo lastOperation={lastTagOperation} />
            {selectedTagId && (
              <>
                <p className="text-gray-600 mt-16 mb-16">
                  Hinnan muutosten alkupäivämäärä
                </p>
                <DatePicker
                  locale={fi}
                  dateFormat="dd.MM.yyyy"
                  onChange={date => setSelectedStartDateForTag(date)}
                  className="form-control"
                  selected={selectedStartDateForTag}
                  showWeekNumbers
                  showYearDropdown
                  scrollableYearDropdown
                />
                <p className="text-gray-600 mt-16 mb-16">
                  Hinnan muutosten päättymispäivämäärä
                </p>
                <DatePicker
                  locale={fi}
                  dateFormat="dd.MM.yyyy"
                  onChange={date => setSelectedEndDateForTag(date)}
                  className="form-control"
                  selected={selectedEndDateForTag}
                  minDate={selectedStartDateForTag ?? null}
                  disabled={!selectedStartDateForTag}
                  showYearDropdown
                  showWeekNumbers
                  scrollableYearDropdown
                />
                <p className="text-gray-600 mt-16 mb-16">
                  Hinnan muutosprosentti
                </p>
                <Form.Control
                  type="number"
                  id="priceChangePercent"
                  name="priceChangePercent"
                  className="mb-16"
                  onChange={e =>
                    setSelectedPriceChangePercentForTag(
                      parseFloat(e.target.value),
                    )
                  }
                />
                <p className="text-gray-600 mt-16 mb-16">
                  Hinnan kiinteä muutos
                </p>
                <Form.Control
                  type="number"
                  id="priceChangeAbsolute"
                  name="priceChangeAbsolute"
                  className="mb-16"
                  onChange={e =>
                    setSelectedPriceChangeAbsoluteForTag(
                      parseFloat(e.target.value),
                    )
                  }
                />
                <InputCheck
                  Id="doUpdatePrices"
                  Name="doUpdatePrices"
                  Label="Korota myyntihinnat"
                  HandleChange={e =>
                    setSelectedDoUpdatePricesForTag(e.target.checked)
                  }
                  Checked={selectedDoUpdatePricesForTag}
                  IsRequired={false}
                />
                <InputCheck
                  Id="doUpdateVatPurchasePrices"
                  Name="doUpdateVatPurchasePrices"
                  Label="Korota alv velvollisten omistajien ostohinnat"
                  HandleChange={e =>
                    setSelectedDoUpdateVatPurchasePricesForTag(e.target.checked)
                  }
                  Checked={selectedDoUpdateVatPurchasePricesForTag}
                  IsRequired={false}
                />
                <InputCheck
                  Id="doUpdateWithoutVatPurchasePrices"
                  Name="doUpdateWithoutVatPurchasePrices"
                  Label="Korota alvittomien omistajien ostohinnat"
                  HandleChange={e =>
                    setSelectedDoUpdateWithoutVatPurchasePricesForTag(
                      e.target.checked,
                    )
                  }
                  Checked={selectedDoUpdateWithoutVatPurchasePricesForTag}
                  IsRequired={false}
                />
                <Button
                  variant="primary"
                  className="mt-16 mb-16 w-100"
                  onClick={handlePriceChangesForTag}
                  disabled={isCopying}
                >
                  {isCopying
                    ? 'Muutetaan hintoja...'
                    : 'Muuta hinnat tagin kohteille'}
                </Button>
              </>
            )}
          </div>
        </div>
        <div className="my-48">
          <div
            className="border bg-white rounded shadow-lg mx-auto p-24 p-xl-32"
            style={{ maxWidth: 640 }}
          >
            <AccommodationSelector
              Subtitle="Hintojen muutos tietylle kohteelle"
              Title="Valitse kohde"
              onAccommodationSelected={handleAccommodationSelected}
            />
            {selectedAccommodationId && (
              <>
                <p className="text-gray-600 mt-16 mb-16">
                  Hinnan muutosten alkupäivämäärä
                </p>
                <DatePicker
                  locale={fi}
                  dateFormat="dd.MM.yyyy"
                  onChange={date => setSelectedStartDateForAccommodation(date)}
                  className="form-control"
                  selected={selectedStartDateForAccommodation}
                  showWeekNumbers
                  showYearDropdown
                  scrollableYearDropdown
                />
                <p className="text-gray-600 mt-16 mb-16">
                  Hinnan muutosten päättymispäivämäärä
                </p>
                <DatePicker
                  locale={fi}
                  dateFormat="dd.MM.yyyy"
                  showYearDropdown
                  onChange={date => setSelectedEndDateForAccommodation(date)}
                  className="form-control"
                  selected={selectedEndDateForAccommodation}
                  minDate={selectedStartDateForAccommodation ?? null}
                  disabled={!selectedStartDateForAccommodation}
                  showWeekNumbers
                  scrollableYearDropdown
                />
                <p className="text-gray-600 mt-16 mb-16">
                  Hinnan muutosprosentti
                </p>
                <Form.Control
                  type="number"
                  id="priceChangePercent"
                  name="priceChangePercent"
                  className="mb-16"
                  onChange={e =>
                    setSelectedPriceChangePercentForAccommodation(
                      parseFloat(e.target.value),
                    )
                  }
                />
                <p className="text-gray-600 mt-16 mb-16">
                  Hinnan kiinteä muutos
                </p>
                <Form.Control
                  type="number"
                  id="priceChangeAbsolute"
                  name="priceChangeAbsolute"
                  className="mb-16"
                  onChange={e =>
                    setSelectedPriceChangeAbsoluteForAccommodation(
                      parseFloat(e.target.value),
                    )
                  }
                />
                <input
                  type="hidden"
                  id="doUpdatePrices"
                  name="doUpdatePrices"
                  value="true"
                />
                <input
                  type="hidden"
                  id="doUpdateVatPurchasePrices"
                  name="doUpdateVatPurchasePrices"
                  value="true"
                />
                <input
                  type="hidden"
                  id="doUpdateWithoutVatPurchasePrices"
                  name="doUpdateWithoutVatPurchasePrices"
                  value="true"
                />
                <Button
                  variant="primary"
                  className="mt-16 mb-16 w-100"
                  onClick={handlePriceChangesForAccommodation}
                  disabled={isCopying}
                >
                  {isCopying
                    ? 'Muutetaan hintoja...'
                    : 'Muuta hinnat kohteelle'}
                </Button>
              </>
            )}
          </div>
        </div>

        {copyMessages.length > 0 && (
          <Card className="bg-white border shadow-none">
            <h6 className="text-uppercase text-muted m-2">Hintamuutokset</h6>
            <Table striped bordered hover>
              <thead>
                <tr>
                  <th>Kohteen numero</th>
                  <th>Kohteen nimi</th>
                  <th>Kausia päivitetty</th>
                </tr>
              </thead>
              <tbody>
                {copyMessages.map(copyMessage => (
                  <tr key={`${copyMessage.accommodationNumber}`}>
                    <td>{copyMessage.accommodationNumber}</td>
                    <td>{copyMessage.accommodationName}</td>
                    <td>{copyMessage.seasonRatesChangedCount}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card>
        )}

        {seasonRateBatchUpdateDetails && (
          <Accordion>
            {seasonRateBatchUpdateDetails.map((detail, index) => (
              <Accordion.Item
                eventKey={index.toString()}
                key={detail.operationType}
              >
                <Accordion.Header>
                  <div>
                    <h6 className="text-uppercase text-muted m-2">
                      {
                        SEASON_RATE_BATCH_OPERATION_TYPE_DESCRIPTIONS[
                          detail.operationType
                        ]
                      }{' '}
                      -{' '}
                      {format(
                        new Date(detail.operationDate),
                        'dd.MM.yyyy HH:mm:ss',
                      )}
                    </h6>
                    {detail.requestParametersDescription && (
                      <div className="text-muted small ms-2 mb-1">
                        {detail.requestParametersDescription}
                      </div>
                    )}
                  </div>
                </Accordion.Header>
                <Accordion.Body>
                  <Card className="bg-white border shadow-none p-24 p-xl-32">
                    <strong>Valitut parametrit:</strong>{' '}
                    {detail.requestParametersDescription}
                    {detail.updateDetails &&
                      detail.updateDetails.length > 0 && (
                        <Table striped bordered hover>
                          <thead>
                            <tr>
                              <th>Kohteen numero</th>
                              <th>Kohteen nimi</th>
                              <th>Kausia muutettu</th>
                            </tr>
                          </thead>
                          <tbody>
                            {detail.updateDetails.map(updateDetail => (
                              <tr key={updateDetail.accommodationNumber}>
                                <td>{updateDetail.accommodationNumber}</td>
                                <td>{updateDetail.accommodationName}</td>
                                <td>{updateDetail.seasonRatesChangedCount}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      )}
                  </Card>
                </Accordion.Body>
              </Accordion.Item>
            ))}
          </Accordion>
        )}
      </Container>
    </>
  );
};

export default ChangeSeasonRatePrices;
